import { AfterViewInit, Component, HostBinding, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { HeaderComponent } from '../../../shared/header/header.component';
import { RELATORIOS, RelatorioOpcao } from '../relatorios-abas/relatorios-abas.component';
import { RelatoriosService } from '../../../../services/relatorios/relatorios.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { ItemSelecao, ListaSelecaoComponent } from '../../../shared/lista-selecao/lista-selecao.component';
import { RelatorioOfertasResumo, RelOfertasConversao, RelOfertasFiltro, RelOfertasItem, RelOfertasPessoa, RelOfertasParticulares, RelOfertasPlano, RelOfertasUnidade, RelOfertasUpsell, RelOfertasCobranca } from '../../../../interfaces/relatorios.dto';
import {
  Escala, Ordem, OperadorVisao, Preset, ResumoParticulares, Selo,
  formatarTaxa, iso, largurasEtapas, larguraVolume, montarOperadores, ordenar, presetDoPeriodo, resumoParticulares, rotuloPeriodo, seloPlano,
} from './ofertas-particulares.util';

const TAMANHO_PAGINA = 100;
const LINHAS_POR_QUADRO = 10;
const CHAVE_VISTA = 'fila:rel-ofertas:vista';

type Vista = 'grafico' | 'tabela';
type QuadroPaginado = 'especialidade' | 'medico';

// Etapas depois da oferta, todas dentro da população de particulares (bloco 'conversao' da lista nominal).
const ETAPAS: { chave: string; rotulo: string; campo: keyof RelOfertasConversao }[] = [
  { chave: 'viram', rotulo: 'Viram a oferta', campo: 'viramOferta' },
  { chave: 'recusaram', rotulo: 'Recusaram algum plano', campo: 'recusaram' },
  { chave: 'termo', rotulo: 'Termo enviado', campo: 'termo' },
  { chave: 'pagamento', rotulo: 'Foram para pagamento', campo: 'pagamento' },
  { chave: 'na_fila', rotulo: 'Compraram no atendimento', campo: 'naFila' },
];

// Chave e título da lista nominal de cada passo da jornada (mesmas chaves do bloco 'conversao').
const PASSOS: { chave: string; titulo: string; lista: string; definicao: string }[] = [
  { chave: 'particulares', titulo: 'Particulares atendidos', lista: 'Particulares atendidos',
    definicao: 'Pacientes de convênio particular atendidos pela Fila v3 no período (cada paciente conta uma vez por unidade).' },
  { chave: 'viram', titulo: 'Viram a oferta', lista: 'Viram a oferta',
    definicao: 'Particulares para quem a oferta de plano apareceu na tela do operador durante o atendimento.' },
  { chave: 'na_fila', titulo: 'No atendimento', lista: 'Compraram no atendimento',
    definicao: 'Particulares que aderiram a um plano pela Fila, em até 7 dias do atendimento. Taxa de compra = compraram ÷ viram a oferta.' },
  { chave: 'pausa', titulo: 'Na pausa', lista: 'Compraram depois na pausa (até 7 dias)',
    definicao: 'Particulares que aderiram em até 7 dias depois do atendimento, por um operador que estava na pausa Venda Cartão.' },
  { chave: 'depois', titulo: 'Depois, outro canal', lista: 'Compraram depois (até 7 dias)',
    definicao: 'Particulares que aderiram por outro canal (admin, link, app) em até 7 dias depois do atendimento.' },
];

export interface PassoJornada { chave: string; titulo: string; valor: number; sub: string; definicao: string; lista: string; }

const GLOSSARIO: { termo: string; texto: string }[] = [
  { termo: 'Particulares atendidos', texto: 'Pacientes de convênio particular atendidos pela Fila v3 no período (cada paciente conta uma vez por unidade). Quem já é cliente do CartãoSiM fica de fora: o assinante em dia já tem plano, e o em atraso ou bloqueado recebe a regularização, não a oferta.' },
  { termo: 'Viram a oferta', texto: 'Particulares para quem a oferta de plano apareceu na tela do operador durante o atendimento.' },
  { termo: 'Compraram no atendimento', texto: 'Particulares que aderiram a um plano pela Fila, em até 7 dias do atendimento. Taxa de compra = compraram no atendimento ÷ viram a oferta.' },
  { termo: 'Na pausa', texto: 'Compra em até 7 dias depois do atendimento, por um operador na pausa Venda Cartão.' },
  { termo: 'Compraram depois', texto: 'Particulares que aderiram por outro canal (admin, link, app) em até 7 dias depois do atendimento.' },
  { termo: 'Recusaram algum plano', texto: 'Particulares que viram a oferta e recusaram pelo menos um dos planos oferecidos.' },
  { termo: 'Termo enviado', texto: 'Particulares que receberam o termo de adesão para assinar.' },
  { termo: 'Foram para pagamento', texto: 'Particulares que chegaram à etapa de pagamento do plano.' },
  { termo: 'Planos vendidos (pagos)', texto: 'Vendas feitas pela Fila no período que já foram pagas; “Compraram no atendimento” inclui as ainda não pagas.' },
  { termo: 'Conversão total', texto: 'Particulares que viraram assinantes (no atendimento, na pausa ou depois) ÷ particulares atendidos.' },
  { termo: 'Ofertado, Vendido e Taxa de compra por plano', texto: 'Quantas vezes cada plano foi oferecido, quantos foram vendidos e a taxa de compra (vendido ÷ ofertado).' },
  { termo: 'Upsell', texto: 'Assinantes que começaram a trocar para um plano melhor; “Pagas” são as trocas concluídas.' },
  { termo: 'Cobrança de inadimplentes', texto: 'Cobranças de regularização geradas para assinantes com pendência e quanto delas foi pago.' },
  { termo: '% particular', texto: 'Pacientes particulares ÷ atendimentos da especialidade ou do médico.' },
];

const ROTULO_PLANO: Record<string, string> = { platina: 'Platina', black: 'Black', ouro: 'Ouro', outro: 'Outro' };

// Abaixo disso a taxa do plano oscila demais para merecer destaque.
const MINIMO_PARA_DESTAQUE = 10;

const UPSELL_VAZIO: RelOfertasUpsell = { iniciadas: 0, pagas: 0, taxa: 0, porPlanoDestino: [] };
const COBRANCA_VAZIA: RelOfertasCobranca = { geradas: 0, pagas: 0, valorRecuperado: 0, taxa: 0 };

const ORDENS: { valor: Ordem; rotulo: string }[] = [
  { valor: 'volume', rotulo: 'Volume' }, { valor: 'taxa', rotulo: 'Taxa' }, { valor: 'az', rotulo: 'A–Z' },
];

const PRESETS: { valor: Preset; rotulo: string }[] = [
  { valor: 'hoje', rotulo: 'Hoje' }, { valor: 'mes', rotulo: 'Este mês' }, { valor: 'mes_anterior', rotulo: 'Mês anterior' },
];

export interface EtapaVisao { chave: string; rotulo: string; total: number; deQuemViu: string; largura: number; }
export interface PlanoVisao { plano: RelOfertasPlano; nome: string; selo: Selo | null; taxa: string; largura: number; }
export interface LinhaParticulares { linha: RelOfertasParticulares; taxa: string; volume: number; parte: number; }

function lerVista(): Record<string, Vista> {
  try {
    const v = JSON.parse(window.localStorage.getItem(CHAVE_VISTA) ?? '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

@Component({
  selector: 'app-ofertas-particulares',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive, HeaderComponent, ListaSelecaoComponent],
  templateUrl: './ofertas-particulares.component.html',
  styleUrl: './ofertas-particulares.component.scss',
  host: { style: 'display: block' },
})
export class OfertasParticularesComponent implements OnInit, AfterViewInit {
  // Começa pelo perfil do login e liga quando o back diz que o perfil Tasy é de administrador.
  admin: boolean;
  readonly unidadeLogada: string;
  readonly nomeUnidadeLogada: string;
  de: string;
  ate: string;
  unidade: string;
  resumo: RelatorioOfertasResumo | null = null;
  // Filtro do relatório que está na tela; lista e exportação usam este, não o formulário editado.
  aplicado: RelOfertasFiltro | null = null;
  // Unidades que o usuário pode consultar, vindas do back; null enquanto não chegam (ou se a chamada falhar).
  private unidadesDisponiveis: RelOfertasUnidade[] | null = null;
  carregando = false;
  erro = '';
  exportando = false;
  erroExportar = '';

  lista: { titulo: string; bloco: string; chave: string; pagina: number; total: number; itens: RelOfertasPessoa[]; carregando: boolean; erro: string } | null = null;

  vista: Record<string, Vista> = lerVista();
  // Passo da jornada com a explicação aberta embaixo do cartão.
  explicacao: string | null = null;
  pagina: Record<QuadroPaginado, number> = { especialidade: 1, medico: 1 };
  ordem: Record<QuadroPaginado, Ordem> = { especialidade: 'volume', medico: 'volume' };
  escalaEtapas: Escala = 'ampliada';
  ajuda = false;
  naoViramAberto = false;
  // Altura do cabeçalho da Fila, que também é fixo: as pílulas grudam logo abaixo dele.
  @HostBinding('style.--rv-topo') topoFixo = '64px';
  secaoAtiva = 's-plano';

  readonly relatorios: RelatorioOpcao[];
  readonly glossario = GLOSSARIO;
  readonly ordens = ORDENS;
  readonly presets = PRESETS;

  // Descarta a resposta de uma carga já superada por outra.
  private carga = 0;
  // Derivados pesados (waffle, ordenação) só são refeitos quando a entrada muda.
  private memo = new Map<string, { deps: unknown[]; valor: unknown }>();

  constructor(private servico: RelatoriosService, localStorage: LocalStorageService) {
    const perfil = localStorage.get('profile');
    this.admin = perfil === 'Administrador';
    const lideranca = perfil === 'Administrador' || perfil === 'Supervisor';
    this.relatorios = RELATORIOS.filter(r => !r.lideranca || lideranca);
    this.unidadeLogada = String(localStorage.get('currentUnit') ?? '');
    this.nomeUnidadeLogada = localStorage.get('unitName') ?? '';
    const hoje = new Date();
    this.hoje = iso(hoje);
    this.minimo = iso(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
    this.ate = iso(hoje);
    this.de = this.hoje;
    this.unidade = this.unidadeLogada;
  }

  // Mesmas regras do back (RelatorioOfertas.ProblemaPeriodo): até 31 dias, do mês anterior até hoje.
  readonly hoje: string;
  readonly minimo: string;
  static readonly MAX_DIAS = 31;

  private get problemaPeriodo(): string | null {
    if (!this.de || !this.ate) return 'Informe o período.';
    if (this.de > this.ate) return 'A data inicial é depois da final.';
    if (this.ate > this.hoje) return 'A data final não pode ser depois de hoje.';
    if (this.de < this.minimo) return `Só é possível consultar a partir de ${this.minimo.split('-').reverse().join('/')} (mês anterior).`;
    const dias = Math.round((new Date(this.ate + 'T00:00').getTime() - new Date(this.de + 'T00:00').getTime()) / 86400000) + 1;
    if (dias > OfertasParticularesComponent.MAX_DIAS) return `O período máximo é de ${OfertasParticularesComponent.MAX_DIAS} dias.`;
    return null;
  }

  async atalho(tipo: Preset): Promise<void> {
    const d = new Date(this.hoje + 'T00:00');
    if (tipo === 'hoje') { this.de = this.hoje; this.ate = this.hoje; }
    if (tipo === 'mes') { this.de = iso(new Date(d.getFullYear(), d.getMonth(), 1)); this.ate = this.hoje; }
    if (tipo === 'mes_anterior') {
      this.de = this.minimo;
      this.ate = iso(new Date(d.getFullYear(), d.getMonth(), 0));
    }
    await this.carregar();
  }

  get presetAtivo(): Preset | null {
    return presetDoPeriodo(this.de, this.ate, this.hoje);
  }

  get periodoCurto(): string {
    const f = this.aplicado ?? this.filtro;
    return rotuloPeriodo(f.de, f.ate, this.hoje);
  }

  ngOnInit(): void {
    void this.carregar();
    void this.carregarUnidades();
  }

  private async carregarUnidades(): Promise<void> {
    try {
      const r = await this.servico.ofertasUnidades();
      if (!r) return;
      if (r.administrador) this.admin = true;
      this.unidadesDisponiveis = r.unidades ?? [];
    } catch {
      return; // sem a lista, o seletor cai para a unidade logada
    }
    const antes = this.unidade;
    const ids = this.opcoesUnidade.map(u => u.id);
    if (this.unidade !== 'todas' && ids.length && !ids.includes(this.unidade)) this.unidade = ids[0];
    // A primeira carga saiu com a unidade logada, que não está entre as permitidas.
    if (this.unidade !== antes) void this.carregar();
  }

  // Mesma referência enquanto a lista não muda: a lista de seleção recalcula a busca quando os itens trocam.
  private cacheItensUnidade: { chave: string; itens: ItemSelecao[] } | null = null;
  get itensUnidade(): ItemSelecao[] {
    const opcoes = this.opcoesUnidade;
    const chave = (this.admin ? 'A|' : '|') + opcoes.map(u => u.id + ':' + u.nome).join(',');
    if (this.cacheItensUnidade?.chave !== chave) {
      const itens = opcoes.map(u => ({ valor: u.id, rotulo: u.nome }));
      this.cacheItensUnidade = { chave, itens: this.admin ? [{ valor: 'todas', rotulo: 'Todas' }, ...itens] : itens };
    }
    return this.cacheItensUnidade.itens;
  }

  get mostraSeletor(): boolean { return this.admin || this.opcoesUnidade.length > 1; }

  // Sem atendimento da v3 nem evento no período: mostra aviso em vez de uma tela cheia de zeros.
  // A venda na pausa fica fora do funil, então conta à parte.
  get semDados(): boolean {
    const r = this.resumo;
    if (!r) return false;
    return r.porEspecialidade.length === 0
      && r.funil.every(f => f.total === 0)
      && (r.cobranca?.geradas ?? 0) + (r.upsell?.iniciadas ?? 0) + (r.vendaCartao?.abordagens ?? 0) === 0;
  }

  get filtro(): RelOfertasFiltro {
    return { de: this.de, ate: this.ate, unidade: this.mostraSeletor ? this.unidade : this.unidadeLogada };
  }

  async carregar(): Promise<void> {
    const problema = this.problemaPeriodo;
    if (problema) { this.erro = problema; return; }
    const minha = ++this.carga;
    this.carregando = true;
    this.erro = '';
    const filtro = this.filtro;
    try {
      const r = await this.servico.ofertasResumo(filtro);
      if (minha !== this.carga) return;
      // O back pode devolver corpo vazio; não deixa undefined chegar ao template.
      if (!r) throw new Error('Relatório indisponível agora.');
      this.resumo = r;
      this.aplicado = filtro;
      if (r.administrador) this.admin = true;
      this.pagina = { especialidade: 1, medico: 1 };
    } catch (e: any) {
      if (minha !== this.carga) return;
      this.erro = e?.message || 'Relatório indisponível agora.';
    } finally {
      if (minha === this.carga) this.carregando = false;
    }
  }

  get opcoesUnidade(): { id: string; nome: string }[] {
    const logada = this.unidadeLogada ? [{ id: Number(this.unidadeLogada), nome: this.nomeUnidadeLogada || this.unidadeLogada }] : [];
    const lista = this.unidadesDisponiveis?.length ? [...this.unidadesDisponiveis] : logada;
    // A lista do administrador vem de quem já teve atividade na v3; unidade recém-liberada ainda não está nela.
    if (this.admin && logada.length && !lista.some(u => String(u.id) === this.unidadeLogada)) lista.push(logada[0]);
    return lista.map(u => ({ id: String(u.id), nome: u.nome }));
  }

  nomeUnidade(id: number): string {
    return this.resumo?.unidades?.find(u => u.id === id)?.nome ?? String(id);
  }

  pct(parte: number, todo: number): string {
    return formatarTaxa(parte, todo);
  }

  // O projeto não registra o locale pt-BR no Angular, então o pipe currency não serve.
  brl(v: number): string {
    return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  vistaDe(quadro: string): Vista {
    return this.vista[quadro] === 'tabela' ? 'tabela' : 'grafico';
  }

  mudarVista(quadro: string, v: Vista): void {
    this.vista = { ...this.vista, [quadro]: v };
    try {
      window.localStorage.setItem(CHAVE_VISTA, JSON.stringify(this.vista));
    } catch { /* sem armazenamento: a escolha vale só nesta tela */ }
  }

  // Percentual já calculado pelo back: a tela e o CSV mostram o mesmo arredondamento.
  pctPronto(v: number | null | undefined): string {
    return `${(v ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
  }

  private memoizar<T>(nome: string, deps: unknown[], calcular: () => T): T {
    const atual = this.memo.get(nome);
    if (atual && atual.deps.length === deps.length && atual.deps.every((d, i) => d === deps[i])) return atual.valor as T;
    const valor = calcular();
    this.memo.set(nome, { deps, valor });
    return valor;
  }

  // ---------- Navegação ----------

  get secoes(): { id: string; rotulo: string }[] {
    const s = [
      { id: 's-plano', rotulo: 'Visão geral' },
      { id: 's-nao-viram', rotulo: 'Quem não viu' },
      { id: 's-planos', rotulo: 'Por plano' },
      { id: 's-operador', rotulo: 'Unidade e operador' },
      { id: 's-upsell', rotulo: 'Upsell e cobrança' },
    ];
    if (this.resumo?.vendaCartao?.abordagens) s.push({ id: 's-venda-cartao', rotulo: 'Venda cartão' });
    s.push({ id: 's-espec', rotulo: 'Por especialidade' }, { id: 's-medico', rotulo: 'Por médico' });
    return s;
  }

  ngAfterViewInit(): void {
    this.medirCabecalho();
  }

  @HostListener('window:resize')
  medirCabecalho(): void {
    const altura = document.querySelector<HTMLElement>('app-header .cab')?.offsetHeight;
    if (altura) this.topoFixo = `${altura}px`;
  }

  irParaSecao(id: string): void {
    this.secaoAtiva = id;
    if (id === 's-nao-viram') this.naoViramAberto = true;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  porId(_: number, s: { id: string }): string { return s.id; }
  porIndice(i: number): number { return i; }

  // ---------- Venda de plano ----------

  get vendidos(): number {
    return this.resumo?.funil.find(f => f.chave === 'contratada')?.total ?? 0;
  }

  get assinantes(): number {
    const c = this.resumo?.conversao;
    return c ? c.naFila + (c.naPausa ?? 0) + c.depois : 0;
  }

  get comprasFila(): number {
    const c = this.resumo?.conversao;
    return c ? c.naFila + (c.naPausa ?? 0) : 0;
  }

  get naoViramTotal(): number {
    const c = this.resumo?.conversao;
    return c ? Math.max(0, c.particulares - c.viramOferta) : 0;
  }

  get emAtrasoSemRegularizacao(): number {
    const c = this.resumo?.conversao;
    return c?.emAtraso ? c.emAtraso - (c.emAtrasoRegularizacao ?? 0) : 0;
  }

  get jornada(): PassoJornada[] {
    const c = this.resumo?.conversao;
    if (!c) return [];
    const sub: Record<string, string> = {
      particulares: 'passaram pela Fila no período',
      viram: `${this.pct(c.viramOferta, c.particulares)} dos particulares · ${Math.max(0, c.particulares - c.viramOferta)} não viram`,
      na_fila: `${this.pct(c.naFila, c.viramOferta)} de quem viu a oferta`,
      pausa: 'em até 7 dias, na pausa Venda Cartão',
      depois: 'em até 7 dias, por outro canal',
    };
    const valor: Record<string, number> = { particulares: c.particulares, viram: c.viramOferta, na_fila: c.naFila, pausa: c.naPausa ?? 0, depois: c.depois };
    return PASSOS.map(p => ({ ...p, valor: valor[p.chave] ?? 0, sub: sub[p.chave] }));
  }

  get passosTopo(): PassoJornada[] { return this.jornada.filter(p => p.chave === 'particulares' || p.chave === 'viram'); }
  get passosCompra(): PassoJornada[] { return this.jornada.filter(p => p.chave !== 'particulares' && p.chave !== 'viram'); }

  get naoViramComTotal(): RelOfertasItem[] {
    return (this.resumo?.naoViram ?? []).filter(m => m.total > 0);
  }

  // O getter recria os passos a cada ciclo; sem isto o botão ⓘ seria trocado e perderia o foco.
  porChave(_: number, p: { chave: string }): string { return p.chave; }

  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  fecharExplicacao(): void { this.explicacao = null; }

  alternarExplicacao(chave: string): void {
    this.explicacao = this.explicacao === chave ? null : chave;
  }

  abrirPasso(p: PassoJornada): void {
    void this.abrirLista('conversao', p.chave, p.lista);
  }

  get etapas(): EtapaVisao[] {
    const c = this.resumo?.conversao;
    if (!c) return [];
    const totais = ETAPAS.map(e => Number(c[e.campo] ?? 0));
    const larguras = largurasEtapas(totais, this.vistaDe('venda') === 'grafico' ? this.escalaEtapas : 'real');
    return ETAPAS.map((e, i) => ({ chave: e.chave, rotulo: e.rotulo, total: totais[i], deQuemViu: this.pct(totais[i], c.viramOferta), largura: larguras[i] }));
  }

  get maiorEtapa(): number {
    return Math.max(0, ...this.etapas.slice(1).map(e => e.total));
  }

  abrirEtapa(chave: string): void {
    const e = ETAPAS.find(x => x.chave === chave);
    if (e) void this.abrirLista('conversao', e.chave, e.rotulo);
  }

  // ---------- Venda por plano ----------

  rotuloPlano(familia: string): string {
    return ROTULO_PLANO[familia] ?? familia;
  }

  get planos(): RelOfertasPlano[] { return this.resumo?.porPlano ?? []; }

  get planosVisao(): PlanoVisao[] {
    return this.memoizar('planos', [this.resumo], () => {
      const aceito = this.maisAceito;
      const recusado = this.maisRecusado;
      return this.planos.map(p => ({
        plano: p, nome: this.rotuloPlano(p.familia), selo: seloPlano(p, aceito, recusado),
        taxa: p.ofertado ? this.pctPronto(p.aceite) : '—', largura: p.ofertado ? Math.min(100, Math.max(0, p.aceite)) : 0,
      }));
    });
  }

  get totalSemOferta(): number {
    return this.planos.reduce((s, p) => s + (p.vendidoSemOferta ?? 0), 0);
  }

  // Só planos com oferta suficiente concorrem; empate não ganha destaque.
  private destaque(taxa: (p: RelOfertasPlano) => number): string | null {
    const candidatos = this.planos.filter(p => p.familia !== 'outro' && p.ofertado >= MINIMO_PARA_DESTAQUE);
    if (!candidatos.length) return null;
    const maior = Math.max(...candidatos.map(taxa));
    const donos = candidatos.filter(p => taxa(p) === maior);
    return maior > 0 && donos.length === 1 ? donos[0].familia : null;
  }

  get maisAceito(): string | null { return this.destaque(p => p.aceite); }
  get maisRecusado(): string | null { return this.destaque(p => p.recusado / p.ofertado); }

  // ---------- Unidade e operador ----------

  get unidadesVisao(): { id: number; nome: string; taxa: string; assinaram: number; particulares: number }[] {
    return (this.resumo?.conversaoPorUnidade ?? []).map(u => ({
      id: u.unidade, nome: this.nomeUnidade(u.unidade), particulares: u.particulares,
      taxa: u.particulares ? this.pctPronto(u.percentual) : '—', assinaram: u.naFila + (u.naPausa ?? 0) + u.depois,
    }));
  }

  get operadores(): { venderam: OperadorVisao[]; semVenda: OperadorVisao[]; atendidosSemVenda: number } {
    return this.memoizar('operadores', [this.resumo], () => montarOperadores(this.resumo?.conversaoPorOperador ?? []));
  }

  porNome(_: number, o: { nome: string }): string { return o.nome; }

  abrirConversao(chave: string): void {
    const titulo = chave.startsWith('unidade:') ? this.nomeUnidade(Number(chave.slice('unidade:'.length))) : chave.slice('operador:'.length);
    void this.abrirLista('conversao', chave, 'Particulares de ' + titulo);
  }

  // ---------- Upsell, cobrança e venda cartão ----------

  get upsell(): RelOfertasUpsell { return this.resumo?.upsell ?? UPSELL_VAZIO; }
  get cobranca(): RelOfertasCobranca { return this.resumo?.cobranca ?? COBRANCA_VAZIA; }

  get upsellParado(): boolean {
    return this.upsell.iniciadas === 0 && this.upsell.pagas === 0;
  }

  get cobrancaParada(): boolean {
    return this.cobranca.geradas === 0 && this.cobranca.pagas === 0 && !this.cobranca.valorRecuperado;
  }

  abrirVendaCartao(chave: string, titulo: string): void {
    void this.abrirLista('venda_cartao', chave, titulo);
  }

  abrirVendaCartaoOperador(chave: string): void {
    this.abrirVendaCartao(chave, 'Venda cartão de ' + chave.slice('operador:'.length));
  }

  abrirCobranca(chave: string): void {
    void this.abrirLista('regularizacao', chave, chave === 'regularizacoes' ? 'Cobranças geradas' : 'Cobranças pagas');
  }

  // ---------- Especialidade e médico ----------

  private linhas(quadro: QuadroPaginado): RelOfertasParticulares[] {
    const brutas = (quadro === 'especialidade' ? this.resumo?.porEspecialidade : this.resumo?.porMedico) ?? [];
    return this.memoizar('linhas:' + quadro, [brutas, this.ordem[quadro]], () =>
      ordenar(brutas, this.ordem[quadro], l => l.particulares, l => l.atendimentos, l => l.nome));
  }

  ordemDe(quadro: QuadroPaginado): Ordem {
    return this.ordem[quadro];
  }

  ordenarPor(quadro: QuadroPaginado, ordem: Ordem): void {
    this.ordem = { ...this.ordem, [quadro]: ordem };
    this.pagina = { ...this.pagina, [quadro]: 1 };
  }

  resumoDe(quadro: QuadroPaginado): ResumoParticulares {
    const brutas = (quadro === 'especialidade' ? this.resumo?.porEspecialidade : this.resumo?.porMedico) ?? [];
    return this.memoizar('resumo:' + quadro, [brutas], () => resumoParticulares(brutas));
  }

  paginaAtual(quadro: QuadroPaginado): number {
    return this.pagina[quadro];
  }

  paginasDe(quadro: QuadroPaginado): number {
    return Math.max(1, Math.ceil(this.linhas(quadro).length / LINHAS_POR_QUADRO));
  }

  daPagina(quadro: QuadroPaginado): RelOfertasParticulares[] {
    const ini = (this.pagina[quadro] - 1) * LINHAS_POR_QUADRO;
    return this.linhas(quadro).slice(ini, ini + LINHAS_POR_QUADRO);
  }

  // A escala do volume é a da lista inteira, para a barra não mudar de tamanho ao trocar de página.
  linhasVisao(quadro: QuadroPaginado): LinhaParticulares[] {
    const todas = this.linhas(quadro);
    const pagina = this.daPagina(quadro);
    return this.memoizar('visao:' + quadro, [todas, this.pagina[quadro]], () => {
      const maior = Math.max(0, ...todas.map(l => l.atendimentos));
      return pagina.map(l => ({
        linha: l, taxa: this.pct(l.particulares, l.atendimentos), volume: larguraVolume(l.atendimentos, maior),
        parte: l.atendimentos ? Math.min(100, (l.particulares * 100) / l.atendimentos) : 0,
      }));
    });
  }

  irPara(quadro: QuadroPaginado, pagina: number): void {
    this.pagina = { ...this.pagina, [quadro]: Math.min(Math.max(1, pagina), this.paginasDe(quadro)) };
  }

  abrirParticulares(quadro: QuadroPaginado, chave: string): void {
    const e = this.linhas(quadro).find(x => x.chave === chave);
    if (e) void this.abrirLista(quadro, e.chave, e.nome);
  }

  // ---------- Lista nominal e exportação ----------

  async abrirLista(bloco: string, chave: string, titulo: string, pagina = 1): Promise<void> {
    const filtro = this.aplicado;
    if (!filtro) return;
    this.lista = { titulo, bloco, chave, pagina, total: this.lista?.total ?? 0, itens: this.lista?.bloco === bloco && this.lista?.chave === chave ? this.lista.itens : [], carregando: true, erro: '' };
    const atual = this.lista;
    try {
      const r = await this.servico.ofertasLista(filtro, bloco, chave, pagina);
      if (this.lista !== atual) return;
      atual.itens = r.itens;
      atual.total = r.total;
    } catch (e: any) {
      if (this.lista !== atual) return;
      atual.erro = e?.message || 'Lista indisponível agora.';
    } finally {
      atual.carregando = false;
    }
  }

  get paginas(): number {
    return Math.max(1, Math.ceil((this.lista?.total ?? 0) / TAMANHO_PAGINA));
  }

  fecharLista(): void { this.lista = null; }

  async exportar(): Promise<void> {
    const filtro = this.aplicado;
    if (this.exportando || !filtro) return;
    this.exportando = true;
    this.erroExportar = '';
    try {
      await this.servico.exportarOfertas(filtro);
    } catch (e: any) {
      this.erroExportar = e?.message || 'Não foi possível exportar agora.';
    } finally {
      this.exportando = false;
    }
  }
}
